import React, {
  useContext,
  useState,
  createContext,
  useEffect,
  useCallback,
  ChangeEvent
} from 'react';
import {
  FigmaUploadProviderProps,
  FigmaNode,
  ToUpdate
} from '../../redesign/clients/design_assets/types';
import _debounce from 'lodash/debounce';
import { useDesignAssetsState } from './DesignAssets';
import { message } from 'antd';
import * as translations from '../../redesign/clients/design_assets/strings';
import { getLangKey } from 'appJS/models/Application';
import { getFileInfo } from 'appJS/utils/fileListOperations';

const strings = translations[getLangKey()];

// @ts-ignore
//eslint-disable-next-line
const FigmaUploadStateContext: React.Context<FigmaUploadState> = createContext();

const DEBOUNCE_WAIT = 1000;
const ORIGINAL_SCALE = 1;
const DEFAULT_PREVIEW_SCALE: number = 0.35;
export const MINIMUM_PREVIEW_SIZE: number = 20000;
const FIGMA_URL_PATTERN: RegExp = /^https:\/\/www\.figma\.com\/(file|proto|design|board)\/[a-zA-Z0-9_-]+\//;
const LOCAL_MAX_REQUESTS_PER_SECOND = localStorage.getItem(
  'figmaRequestsPerSecond'
);
const MAX_REQUESTS_PER_SECOND = LOCAL_MAX_REQUESTS_PER_SECOND
  ? parseInt(LOCAL_MAX_REQUESTS_PER_SECOND, 10)
  : 35;
const DELAY_BETWEEN_REQUESTS = (60 * 1000) / MAX_REQUESTS_PER_SECOND;

const FigmaUploadProvider = ({ children }: FigmaUploadProviderProps) => {
  const {
    setIsFigmaModalOpen,
    project,
    uploadAsset,
    getFigmaData,
    reAuthFigma,
    reconnectAssetId,
    data
  } = useDesignAssetsState();
  const [nodes, setNodes] = useState<FigmaNode[]>([]);
  //eslint-disable-next-line
  const [nodeID, setNodeID] = useState<string>('');
  //eslint-disable-next-line
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);
  const [loadingNodes, setLoadingNodes] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>('');
  const [fileKey, setFileKey] = useState<string>('');
  const [showUpload, setShowUpload] = useState<boolean>(false);
  const [isImagesLoading, setIsImagesLoading] = useState<boolean>(false);

  const extractFigmaFileId = (url: string) => {
    const figmaURLRegex = /www\.figma\.com\/(file|proto|design|board)\/([^/]+)/;
    const matchFigmaURL = url.match(figmaURLRegex);
    return matchFigmaURL ? matchFigmaURL[2] : undefined;
  };

  const debounceInputOnChange = useCallback(
    _debounce(
      (value: string) => {
        setFileKey(extractFigmaFileId(value) || '');
        setInputValue(value);
      },
      DEBOUNCE_WAIT,
      {
        trailing: true
      }
    ),
    []
  );

  const handleSelect = (isSelected: boolean) => {
    if (reconnectAssetId || !nodes?.length) return;
    setNodes(nodes.map(node => ({ ...node, isSelected })));
  };

  const selectedNodes = nodes.filter(({ isSelected }) => isSelected);

  const progress: number =
    Math.round(
      selectedNodes.reduce(
        (accumulator, { progress }) => accumulator + (progress || 0),
        0
      ) / selectedNodes.length
    ) || 0;

  const uploadImages = async () => {
    setIsImagesLoading(true);

    const allIds = selectedNodes.map(image => image.id).join(',');
    const unscaledImage = await getFigmaData(
      `/images/${fileKey}?ids=${allIds}&format=jpg`
    );
    const imageData = await unscaledImage.json();

    const reconnecting = !!reconnectAssetId;
    const reconnectAsset = reconnecting
      ? data.find(asset => asset.id === reconnectAssetId)
      : undefined;
    const imagesToUpload = reconnecting
      ? selectedNodes.slice(0, 1)
      : selectedNodes;

    imagesToUpload.forEach(async image => {
      const imageUrl = imageData.images[image.id];
      const imageRequest = await fetch(imageUrl);
      const imageBlob = await imageRequest?.blob();
      const fileName = reconnectAsset?.name || `${image?.name}.png`;

      const blobFile = await new File([imageBlob], fileName, {
        type: 'image/png'
      });

      // @ts-expect-error
      const _file = getFileInfo(blobFile);

      if (blobFile) {
        const uploadParameters = {
          file: _file,
          remove: () => {},
          endpoint: `/projects/${project.id}/assets/new`,
          additionalQueryParams: {
            file_name: fileName,
            is_figma: true
          },
          feedback: message.error,
          onStart: () => {},
          onProgress: ({ loaded, total, lengthComputable }: ProgressEvent) => {
            if (lengthComputable)
              //eslint-disable-next-line
              updateNode(image.id, {
                progress: Math.round((loaded / total) * 100)
              });
          },
          onError: () => {},
          onComplete: () => {},
          figmaNodeId: image.id,
          figmaFileKey: fileKey,
          assetId: reconnecting ? reconnectAssetId : undefined
        };
        uploadAsset(uploadParameters);
      }
    });
  };

  const handleError = (response?: any, error?: any) => {
    setHasError(true);
    message.error(strings.figmaUploadError);
    console.error(response ? response.statusText : error);
  };

  const handleRateLimitError = (response: Response) => {
    setHasError(true);
    const retryAfter = response.headers.get('Retry-After');
    const hours = retryAfter
      ? Math.ceil(parseInt(retryAfter, 10) / 3600)
      : undefined;
    message.error(strings.figmaRateLimited(hours), 10);
  };

  const handleNodesResponse = async (_nodeID: string, response?: any) => {
    if (hasError) setHasError(false);
    const nodesData = await response.json();
    const { id, name, children, type } = nodesData.nodes[_nodeID].document;
    const isSelected: boolean = false;
    const _nodes = children.map(({ id, type, name }) => ({
      id,
      type,
      name,
      isSelected
    }));
    _nodes.unshift({ id, name, type, isSelected });
    setNodes(_nodes);
  };

  const getNodesFromLink = async () => {
    let url;

    try {
      url = new URL(inputValue);
    } catch (error) {
      console.error(error);
      message.error(strings.invalidUrl);
      return;
    }

    if (url && FIGMA_URL_PATTERN.test(inputValue)) {
      let response;

      const pageId = url.searchParams.get('page-id');
      const nodeId = url.searchParams.get('node-id');
      let _nodeID = '';

      if (pageId || nodeId) {
        _nodeID = (pageId || nodeId).replace('-', ':');
      }

      if (!_nodeID) return message.error(strings.invalidUrl);

      setNodeID(_nodeID);

      try {
        setLoadingNodes(true);
        const nodesParams = `/files/${extractFigmaFileId(
          inputValue
        )}/nodes?ids=${_nodeID}&depth=1`;
        response = await getFigmaData(nodesParams);
        if (response.status === 200) {
          handleNodesResponse(_nodeID, response);
        } else if (response.status === 404) {
          reAuthFigma();
        } else if (response.status === 429) {
          handleRateLimitError(response);
        } else {
          handleError(response);
        }
      } catch (error) {
        handleError(response, error);
      } finally {
        setLoadingNodes(false);
      }
    } else {
      setHasError(true);
      setFileKey('');
      if (inputValue) message.error(strings.figmaUploadError);
    }
  };

  const getNodeImageURLs = async () => {
    setShowUpload(true);
    const nodesClone = nodes.slice();
    let imageData;
    let imageUrls;
    try {
      const allIds = nodes.map(node => node.id).join(',');

      imageData = await getFigmaData(
        `/images/${fileKey}?ids=${allIds}&scale=${DEFAULT_PREVIEW_SCALE}`
      );
      if (imageData.status === 429) {
        handleRateLimitError(imageData);
        setShowUpload(false);
        setIsFigmaModalOpen(false);
        return;
      }
      const previewResponse = await imageData.json();

      const blobResults = await Promise.all(
        nodes.map(async node => {
          const imageURL = previewResponse.images[node.id];
          const imageRequest = await fetch(imageURL);
          const imageBlob = await imageRequest?.blob();
          return { id: node.id, imageURL, blobSize: imageBlob.size };
        })
      );

      const smallNodeIds = blobResults
        .filter(r => r.blobSize < MINIMUM_PREVIEW_SIZE)
        .map(r => r.id);

      if (smallNodeIds.length > 0) {
        imageData = await getFigmaData(
          `/images/${fileKey}?ids=${smallNodeIds.join(',')}&scale=${ORIGINAL_SCALE}`
        );
        if (imageData.status === 429) {
          handleRateLimitError(imageData);
          setShowUpload(false);
          setIsFigmaModalOpen(false);
          return;
        }
        const fullScaleResponse = await imageData.json();
        smallNodeIds.forEach(id => {
          const result = blobResults.find(r => r.id === id);
          if (result) result.imageURL = fullScaleResponse.images[id];
        });
      }

      imageUrls = blobResults.map(({ id, imageURL }) => ({ id, imageURL }));
    } catch (error) {
      handleError(imageData, error);
      setShowUpload(false);
      setIsFigmaModalOpen(false);
    }
    if (imageUrls?.length) {
      setNodes(
        nodesClone.map(node => ({
          ...node,
          imageUrl: imageUrls.find(({ id }) => id === node.id)?.imageURL
        }))
      );
    }
  };

  const updateNode = (_nodeID: string, toUpdate: ToUpdate) => {
    setNodes(
      nodes.map(node => {
        if (reconnectAssetId && toUpdate.isSelected) {
          return node.id === _nodeID
            ? { ...node, ...toUpdate }
            : { ...node, isSelected: false };
        }
        return node.id === _nodeID ? { ...node, ...toUpdate } : node;
      })
    );
  };

  useEffect(() => {
    if (inputValue) {
      if (FIGMA_URL_PATTERN.test(inputValue)) {
        getNodesFromLink();
      } else {
        message.error(strings.invalidUrl);
      }
    }
  }, [inputValue]);

  const onInputChange = (event: ChangeEvent<HTMLInputElement>) =>
    debounceInputOnChange(event.target.value);

  const uploadedImages = selectedNodes.filter(
    ({ progress }) => progress === 100
  );

  useEffect(() => {
    if (progress === 100) {
      setIsFigmaModalOpen(false);
      setNodes([]);
      setShowUpload(false);
    }
  }, [progress]);

  return (
    <FigmaUploadStateContext.Provider
      value={{
        nodes,
        setNodes,
        fileKey,
        updateNode,
        onInputChange,
        hasError,
        inputValue,
        getNodeImageURLs,
        loadingNodes,
        isLoading,
        showUpload,
        setShowUpload,
        selectedNodes,
        progress,
        uploadImages,
        handleSelect,
        isImagesLoading,
        uploadedImages,
        setFileKey
      }}
    >
      {children}
    </FigmaUploadStateContext.Provider>
  );
};

type FigmaUploadState = {
  nodes: FigmaNode[];
  setNodes: (nodes: FigmaNode[]) => void;
  handleClose: () => void;
  fileKey: string;
  updateNode: (nodeId: string, attributes: ToUpdate) => void;
  onInputChange: (event: ChangeEvent<HTMLInputElement>) => void | undefined;
  hasError: boolean;
  inputValue: string;
  getNodeImageURLs: () => Promise<void>;
  loadingNodes: boolean;
  isLoading: boolean;
  showUpload: boolean;
  setShowUpload: (showUpload: boolean) => void;
  selectedNodes: FigmaNode[];
  progress: number;
  uploadImages: () => Promise<void>;
  handleSelect: (isSelected: boolean) => void;
  isImagesLoading: boolean;
  uploadedImages: FigmaNode[];
  setFileKey: (fileKey: string) => void;
};

const useFigmaUploadState = (): FigmaUploadState => {
  const context = useContext(FigmaUploadStateContext);

  if (context === undefined) {
    throw new Error(
      'useFigmaUploadState must be used within a FigmaUploadProvider'
    );
  }

  // @ts-ignore
  return context;
};

export { FigmaUploadProvider, useFigmaUploadState, FigmaUploadState };
